import { useQuery } from "@tanstack/react-query";
import { axiosInterceptor } from "app/services/http-services/request-interceptor";
import {
    FlussMetadataOption,
    ManualTriggerBusinessRuleOption,
} from "./fluss-node-panel-data";

interface MetadataEnvelope {
    data: FlussMetadataOption[];
}

export const listFlussMetadata = async (
    tableName: string,
    params?: Record<string, string>
) => {
    const response = await axiosInterceptor.get<MetadataEnvelope>(
        `/api/fluss/metadata/${tableName}`,
        { params }
    );
    return response.data.data;
};

export const listManualTriggerBusinessRules = async (
    definitionKey: string,
    startElementId: string
) => {
    const response = await axiosInterceptor.get<{ data: ManualTriggerBusinessRuleOption[] }>(
        "/api/fluss/metadata/manual_trigger_business_rules",
        { params: { definition_key: definitionKey, start_element_id: startElementId } }
    );
    return response.data.data;
};

// ACH-046 (design_prim_pilot_045.md): the End Event "Final status"/"Named final status"
// dropdowns used to hardcode a fixed four-value list in
// EndNodePanels.tsx -- the exact same class of bug already fixed once for
// listOutcomeVocabulary above. Read from the backend
// (Fluss::Api::MetadataController#anchor_final_statuses, derived from the anchor table's real
// `status` column and its real sys_choice options), never hardcoded here. A hardcoded
// TypeScript copy would silently drift from whatever the chosen anchor table actually accepts,
// recreating this exact bug.
export interface FlussAnchorFinalStatuses {
    table: string;
    column: string | null;
    column_type: string | null;
    options: { value: string; label: string }[];
}

export const listAnchorFinalStatuses = async (
    tableName: string
): Promise<FlussAnchorFinalStatuses | null> => {
    const response = await axiosInterceptor.get<{ data: FlussAnchorFinalStatuses | null }>(
        "/api/fluss/metadata/anchor_final_statuses",
        { params: { table: tableName } }
    );
    return response.data.data;
};

export const useAnchorFinalStatuses = (tableName: string) => useQuery({
    queryKey: ["fluss", "node-panel", "anchor-final-statuses", tableName],
    queryFn: () => listAnchorFinalStatuses(tableName),
    enabled: Boolean(tableName),
});

export const useFlussTableColumns = (
    tableName: string,
    tables: FlussMetadataOption[]
) => {
    const table = tables.find((candidate) => candidate.name === tableName);
    return useQuery({
        queryKey: ["fluss", "node-panel", "columns", table?.id],
        queryFn: () => listFlussMetadata("sys_columns", { sys_table_id: table?.id || "" }),
        enabled: Boolean(table?.id),
    });
};
